import * as React from "react";
import { ChevronDown, Sparkles } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { cn } from "../../lib/cn";

export interface ActionDrawerReasoningProps {
  /** Reasoning trace lines — one bullet per inference step. */
  steps: readonly string[];
  /** Citations referenced by `[n]` markers in the reasoning. */
  citations?: ReadonlyArray<{ id: string; label: string }>;
  /** Default expanded? Default: false (collapsed). */
  defaultOpen?: boolean;
  className?: string;
}

/**
 * Reasoning trace zone — "Perché Ando propone questi valori".
 *
 * Collapsible by default to keep the form chrome quiet. When open, lists the
 * inference steps + optional source citations (clickable in feature wire-up
 * to the Source Drawer A.11).
 */
export const ActionDrawerReasoning: React.FC<ActionDrawerReasoningProps> = ({
  steps,
  citations,
  defaultOpen = false,
  className,
}) => {
  const { i18n } = useLingui();
  const [open, setOpen] = React.useState(defaultOpen);
  if (steps.length === 0) return null;

  return (
    <div className={cn("rounded-md border border-hairline bg-cream-deep/40", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left font-medium text-ink-2 text-xs hover:text-ink-1"
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-accent" />
          {i18n._(msg`Perché Ando propone questi valori`)}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 transition-transform duration-150",
            open && "rotate-180",
          )}
        />
      </button>
      {open ? (
        <div className="border-hairline border-t px-3 py-2.5">
          <ol className="space-y-1.5 text-ink-1 text-xs">
            {steps.map((step, i) => (
              <li key={`${i}-${step.slice(0, 32)}`} className="flex gap-2">
                <span className="shrink-0 text-ink-3">{i + 1}.</span>
                <span className="leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
          {citations && citations.length > 0 ? (
            <div className="mt-3 border-hairline border-t pt-2">
              <p className="font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
                {i18n._(msg`Fonti`)}
              </p>
              <ul className="mt-1.5 flex flex-wrap gap-1.5">
                {citations.map((c) => (
                  <li
                    key={c.id}
                    className="inline-flex items-center gap-1 rounded-sm bg-ando-orange-soft px-1.5 py-0.5 text-[11px] text-accent"
                  >
                    <span className="font-mono">[{c.id}]</span>
                    <span className="max-w-[160px] truncate">{c.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
ActionDrawerReasoning.displayName = "ActionDrawerReasoning";
